import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts';
import { Results } from '../../types/results';

interface Props {
  results: Results;
}

const ProfileTab: React.FC<Props> = ({ results }) => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const percentage = Math.round((results.scores.raw_score / results.scores.max_score) * 100);

  // Get tempo label in French
  const tempoLabel = results.profile.tempo_label || results.profile.tempo;

  return (
    <div className="space-y-4">
      {/* Profile Card with Gradient */}
      <div className="bg-gradient-to-br from-primary to-blue-700 rounded-xl p-5 text-white">
        {/* Header */}
        <div className={`flex justify-between items-start mb-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div className="flex-1">
            <p className="text-blue-100 text-xs uppercase tracking-wide mb-1">{t('results.yourProfile') || 'Your Profile'}</p>
            <h2 className="text-xl sm:text-2xl font-bold leading-tight">
              {results.profile.emoji} {results.profile.level} {tempoLabel}
            </h2>
            <p className="text-blue-200 text-sm mt-1">{t('results.profileNum') || 'Profile'} {results.profile.profile_number} • {results.profile.profile_name}</p>
          </div>
          <div className={isRTL ? 'text-left' : 'text-right'}>
            <p className="text-3xl font-bold">{percentage}%</p>
            <p className="text-blue-200 text-xs">{t('results.successRate') || 'Success Rate'}</p>
          </div>
        </div>

        {/* Score Range */}
        <div className="bg-white bg-opacity-15 rounded-lg px-4 py-3">
          <div className={`flex items-center gap-3 text-sm ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-blue-50 text-xs sm:text-sm">
              {t('results.scoreRange') || 'Score Range'}: {results.profile.score_range} | {t('results.percentile')}: {results.scores.percentile}%
            </p>
          </div>
        </div>
      </div>

      {/* Conclusion - Separate Section */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className={`font-semibold text-gray-800 mb-2 flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          {t('results.conclusion')}
        </h4>
        <p className="text-sm text-gray-700 leading-relaxed">{results.profile.conclusion}</p>
      </div>

      {/* Action Plan - Only show if not null */}
      {results.profile.action && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className={`font-semibold text-blue-900 mb-2 flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
            {t('results.actionPlan')}
          </h4>
          <p className="text-sm text-blue-800 leading-relaxed">{results.profile.action}</p>
        </div>
      )}

      {/* Strengths */}
      {results.dimension_analysis.strengths.length > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <h4 className={`font-semibold text-green-900 mb-2 flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {t('results.strengths') || 'Strengths'}
          </h4>
          <ul className="space-y-1.5">
            {results.dimension_analysis.strengths.map((item, i) => (
              <li key={i} className="text-sm text-green-800 flex items-start gap-2">
                <span className="text-green-500 mt-0.5">•</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Areas for Improvement */}
      {results.dimension_analysis.areas_for_improvement.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <h4 className={`font-semibold text-amber-900 mb-2 flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            {t('results.improvementAreas')}
          </h4>
          <ul className="space-y-1.5">
            {results.dimension_analysis.areas_for_improvement.map((item, i) => (
              <li key={i} className="text-sm text-amber-800 flex items-start gap-2">
                <span className="text-amber-500 mt-0.5">•</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ProfileTab;
